import { useState } from 'react';
import { Copy, Phone, Star, Trash2, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatPhoneNumber } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { usePhoneCall } from '@/components/phone/PhoneCallProvider';
import { captureError } from '@/lib/sentry';

const PHONE_LABEL_OPTIONS = ['mobile', 'main', 'business', 'home', 'fax', 'assistant', 'additional'] as const;

interface ContactPhonesProps {
  contactId: string;
  contactName: string;
  phones: Array<{
    id: number;
    phone_number: string;
    isPrimary: boolean;
    label?: string;
  }>;
  onUpdate: () => void;
}

export function ContactPhones({ contactId, contactName, phones, onUpdate }: ContactPhonesProps) {
  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('mobile');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedPhone, setEditedPhone] = useState('');
  const [editedLabel, setEditedLabel] = useState('mobile');
  const { toast } = useToast();
  const { startCall } = usePhoneCall();

  const addPhone = async () => {
    try {
      const { error } = await supabase.from('phone_numbers').insert({
        contact_id: contactId,
        phone_number: newPhone,
        isPrimary: phones.length === 0,
        label: newLabel,
      });

      if (error) throw error;

      setNewPhone('');
      setNewLabel('mobile');
      setIsAdding(false);
      onUpdate();
      
      toast({
        description: "Phone number added successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-phones-add-phone' });
      toast({
        title: "Error",
        description: "Failed to add phone number",
        variant: "destructive",
      });
    }
  };

  const deletePhone = async (id: number) => {
    try {
      const { error } = await supabase
        .from('phone_numbers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      
      toast({
        description: "Phone number removed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-phones-delete-phone' });
      toast({
        title: "Error",
        description: "Failed to remove phone number",
        variant: "destructive",
      });
    }
  };

  const setPrimary = async (id: number) => {
    try {
      // First, set all phones to non-primary
      const { error: resetError } = await supabase
        .from('phone_numbers')
        .update({ isPrimary: false })
        .eq('contact_id', contactId);

      if (resetError) throw resetError;

      // Then set the selected phone as primary
      const { error: updateError } = await supabase
        .from('phone_numbers')
        .update({ isPrimary: true })
        .eq('id', id);

      if (updateError) throw updateError;

      onUpdate();
      
      toast({
        description: "Primary phone updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-phones-set-primary' });
      toast({
        title: "Error",
        description: "Failed to update primary phone",
        variant: "destructive",
      });
    }
  };

  const updatePhone = async (id: number) => {
    try {
      const { error } = await supabase
        .from('phone_numbers')
        .update({ phone_number: editedPhone, label: editedLabel })
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      setEditingId(null);
      
      toast({
        description: "Phone number updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-phones-update-phone' });
      toast({
        title: "Error",
        description: "Failed to update phone number",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="font-semibold text-sm">Phone Numbers</h2>

      {phones.map((phone) => (
        <div
          key={phone.id}
          className="group flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            {editingId === phone.id ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  className="h-7 text-xs w-[180px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updatePhone(phone.id);
                    } else if (e.key === 'Escape') {
                      setEditingId(null);
                    }
                  }}
                />
                <Select value={editedLabel} onValueChange={setEditedLabel}>
                  <SelectTrigger className="h-7 text-xs w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHONE_LABEL_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => updatePhone(phone.id)} className="h-7 text-xs">Save</Button>
                <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="h-7 text-xs">Cancel</Button>
              </div>
            ) : (
              <a
                href={`tel:${phone.phone_number}`}
                onClick={(e) => {
                  e.preventDefault();
                  startCall(contactId, phone.phone_number, contactName);
                  const telLink = document.createElement('a');
                  telLink.href = `tel:${phone.phone_number}`;
                  telLink.click();
                }}
                className="text-xs hover:underline"
              >
                {formatPhoneNumber(phone.phone_number)}
              </a>
            )}
            {phone.isPrimary && (
              <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />
            )}
            {phone.label && phone.label !== 'additional' && (
              <Badge variant="secondary" className="text-xs capitalize">{phone.label}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setEditingId(phone.id);
                setEditedPhone(phone.phone_number);
                setEditedLabel(phone.label || 'mobile');
              }}
              className="h-6 w-6"
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigator.clipboard.writeText(phone.phone_number)}
              className="h-6 w-6"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            {!phone.isPrimary && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPrimary(phone.id)}
                className="h-6 w-6"
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => deletePhone(phone.id)}
              className="h-6 w-6 text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}

      {isAdding ? (
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newPhone.trim()) addPhone();
              if (e.key === 'Escape') setIsAdding(false);
            }}
            placeholder="Enter phone number"
            className="h-7 text-xs flex-1"
          />
          <Select value={newLabel} onValueChange={setNewLabel}>
            <SelectTrigger className="h-7 text-xs w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHONE_LABEL_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={addPhone}
            disabled={!newPhone.trim()}
            className="h-7 text-xs"
          >
            Add
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(false)}
            className="h-7 text-xs"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Phone Number
        </Button>
      )}
    </div>
  );
}